// @flow
import {atom, computed} from 'nanostores';

import {createStoresContext} from '../../../../../utils/react/nanostoresContext';
import {arrayMove} from '../../../../../utils/array';

import type {
  ProductOptionGroup,
  ProductOptionGroupOptionConfig,
} from '../../../../../entities/product';
import type {
  StoreProductOption,
  StoreProductOptionGroup,
} from '../../../../../entities/product-option';

type FormProductOptionState = {
  groups?: ProductOptionGroup[],
  enabled: boolean,
};

type StoreProductOptionsByGroupId = {
  [key: string]: StoreProductOption,
};

export const {
  StoresProvider: StoreOptionProvider,
  useActions,
  useStoreSelector,
} = createStoresContext<FormProductOptionState>(initialState => {
  const $enabled = atom<boolean>(initialState.enabled);
  const $groups = atom<ProductOptionGroup[]>(initialState.groups || []);
  const $groupIds = computed($groups, groups =>
    groups.map(group => group.storeProductOptionGroup.id)
  );

  const $storeGroupOptions = atom<StoreProductOptionsByGroupId>({});

  return {
    enabled: $enabled,
    groups: $groups,
    groupIds: $groupIds,
    storeGroupOptions: $storeGroupOptions,
  };
});

type GroupConfiguration = {
  presentationType?: string,
  required?: boolean,
  minQuantity?: number,
  maxQuantity?: number,
};

type OptionUpdate = {
  useDefault?: boolean,
  enabled?: boolean,
  price?: number,
};

export function useStoreOptionActions() {
  const actions = useActions(s => ({
    /* ---- STORE GROUP OPTIONS ACTIONS ---- */
    addNewStoreGroupOption: (groupId: string, option: StoreProductOption[]) => {
      const prevOptions = s.storeGroupOptions.get();
      s.storeGroupOptions.set({...prevOptions, [groupId]: option});
    },
    addBatchStoreGroupOptions: (groups: StoreProductOptionGroup[]) => {
      const prevOptions = s.storeGroupOptions.get();

      const newOptions = groups.reduce((acc, group) => {
        acc[group.id] = group.options;
        return acc;
      }, {});

      s.storeGroupOptions.set({...prevOptions, ...newOptions});
    },
    removeStoreGroupOption: (groupId: string) => {
      const prevOptions = s.storeGroupOptions.get();
      s.storeGroupOptions.set({...prevOptions, [groupId]: undefined});
    },
    /* ---- STORE PRODUCT OPTIONS ACTIONS ---- */
    setProductOptionEnabled: (enabled: boolean) => {
      s.enabled.set(enabled);
    },
    /* ---- GROUP ACTIONS ---- */
    addNewOptionGroup: (group: ProductOptionGroup[]) => {
      s.groups.set([...s.groups.get(), ...group]);
    },
    removeOptionGroup: (groupId: string) => {
      const groups = s.groups.get();
      s.groups.set(groups.filter(group => group.id !== groupId));
    },
    orderGroups: (oldIndex: number, newIndex: number) => {
      const groups = s.groups.get();
      const reorderedGroups = arrayMove(groups, oldIndex, newIndex);
      s.groups.set(reorderedGroups);
    },
    setOptionGroupEnabled: (groupId: string, enabled: boolean) => {
      const groups = s.groups.get();
      const newGroups = groups.map(group => {
        if (group.id === groupId) {
          return {
            ...group,
            enabled,
          };
        }
        return group;
      });
      s.groups.set(newGroups);
    },
    /* ---- OPTION ACTIONS ---- */
    orderGroupOptions: (
      groupId: string,
      oldIndex: number,
      newIndex: number
    ) => {
      const groups = s.groups.get();
      const group = groups.find(group => group.id === groupId);

      const reorderedOptions = arrayMove(group.options, oldIndex, newIndex);

      const newGroups = groups.map(group => {
        if (group.id === groupId) {
          return {
            ...group,
            options: reorderedOptions,
          };
        }
        return group;
      });
      s.groups.set(newGroups);
    },
    changeGroupConfiguration: (
      groupId: string,
      configuration: GroupConfiguration
    ) => {
      const groups = s.groups.get();
      const newGroups = groups.map(group => {
        if (group.id === groupId) {
          const fieldType = configuration.fieldType ?? group.fieldType;

          return {
            ...group,
            fieldType,
            required: configuration.required ?? group.required,
            config: {
              ...group.config,
              minQuantity:
                configuration.minQuantity ?? group.config?.minQuantity,
              maxQuantity:
                configuration.maxQuantity ?? group.config?.maxQuantity,
              presentationType:
                fieldType !== 'MULTI_OPTION'
                  ? 'LISTING'
                  : configuration.presentationType ??
                    group.config?.presentationType ??
                    'LISTING',
            },
          };
        }
        return group;
      });
      s.groups.set(newGroups);
    },
    updateOptionInGroups: (
      groupId: string,
      optionId: string,
      option: OptionUpdate
    ) => {
      const groups = s.groups.get();

      const newGroups = groups.map(group => {
        if (group.id !== groupId) return group;
        return {
          ...group,
          options: group.options.map(opt =>
            opt.id === optionId ? {...opt, ...option} : opt
          ),
        };
      });
      s.groups.set(newGroups);
    },
    removeOption: (groupId: string, optionId: string) => {
      const groups = s.groups.get();
      const newGroups = groups.map(group => {
        if (group.id !== groupId) return group;
        return {
          ...group,
          options: group.options.filter(option => option.id !== optionId),
        };
      });
      s.groups.set(newGroups);
    },
    addNewOptions: (
      groupId: string,
      options: ProductOptionGroupOptionConfig[]
    ) => {
      const groups = s.groups.get();
      const newGroups = groups.map(group => {
        if (group.id !== groupId) return group;
        return {
          ...group,
          options: [...group.options, ...options],
        };
      });
      s.groups.set(newGroups);
    },
  }));

  return actions;
}
